import React, {
  MouseEvent,
  useMemo,
  useState,
} from 'react';
import {
  MultiMediaResponse,
  PatientMediaCategoryDTO,
} from 'dtos';
import {
  useTableContext,
  BaseTableContext,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/TableContext';
import {
  formatDate,
  formatDateTime,
} from 'utils/date';
import ToolTip from 'core/components/Tooltip';
import useDeleteMediaHandler from 'pages/Dashboard/hooks/useDeleteMediaHandler';
import {
  useNotify,
} from 'core/hooks';
import {
  getErrorMessage,
} from 'utils/errors';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from 'core/icons/delete';
import DownloadIcon from 'core/icons/download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import SignatureIcon from '@mui/icons-material/BorderColorRounded';
import CircularProgress from '@mui/material/CircularProgress';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  putApiMultimediaUpdatePatientMultiMedia,
  getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId,
} from 'endpoints';
import Skeleton from 'core/components/Skeleton';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
  getFileExtension,
  getFileParts,
} from '@populateui/utils';
import head from 'lodash/head';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  downloadFileFromUrl,
} from 'pages/Dashboard/utils/helper';
import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import {
  useGetMediaSettings,
} from 'pages/Dashboard/hooks/useUploadPatientMedia';
import MediaTypeMenu, {
  Category,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/MediaTypeMenu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';

export type MultimediaContextType = BaseTableContext & {
  categories: PatientMediaCategoryDTO[];
};

type FileTypeIcon = {
  icon: typeof InsertDriveFileIcon;
  color?: string;
};

export const FILE_TYPE_ICONS: Record<
  string,
  FileTypeIcon
 > = {
   // Default
   default: { icon: InsertDriveFileIcon },
   // PDF
   pdf: { icon: PictureAsPdfIcon },
   // Images
   jpg: { icon: ImageIcon },
   jpeg: { icon: ImageIcon },
   png: { icon: ImageIcon },
   gif: { icon: ImageIcon },
   svg: { icon: ImageIcon },
   webp: { icon: ImageIcon },
   bmp: { icon: ImageIcon },
   heic: { icon: ImageIcon },
   tif: { icon: ImageIcon },
   tiff: { icon: ImageIcon },
   dcm: { icon: ImageIcon },
   // Documents
   doc: { icon: DescriptionIcon },
   docx: { icon: DescriptionIcon },
   rtf: { icon: DescriptionIcon },
   odt: { icon: DescriptionIcon },
   txt: { icon: DescriptionIcon },
   // Spreadsheets
   xls: { icon: TableChartIcon },
   xlsx: { icon: TableChartIcon },
   ods: { icon: TableChartIcon },
   // Code/Markup
   htm: { icon: CodeIcon },
   html: { icon: CodeIcon },
   xml: { icon: CodeIcon },
 };

export function getFileIcon(fileName: string | null | undefined) {
  const extension = getFileExtension(fileName ?? '');
  const fileType = extension ? FILE_TYPE_ICONS[extension] : null;

  const IconComponent = fileType?.icon ?? InsertDriveFileIcon;
  return (
    <IconComponent
      className={clsx(
        '!w-5 !h-5 flex-shrink-0',
        fileType?.color ?? 'text-gray-500',
      )}
    />
  );
}

type CategoryMenuProps = {
  media: MultiMediaResponse;
  categories: PatientMediaCategoryDTO[];
  onUpdate?: () => void;
  isEditable: boolean;
};

function CategoryMenu({ media, onUpdate, isEditable }: CategoryMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const notify = useNotify();

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onMediaTypeChange = async ({ mediaType, categoryName }: Category) => {
    if (
      !isNil(media.mediaId)
      && notEmptyString(media.fileName ?? '')
      && !isNil(media.timeStamp)
      && !isUpdating
    ) {
      setIsUpdating(true);
      handleMenuClose();

      try {
        await putApiMultimediaUpdatePatientMultiMedia({
          patientMultiMediaId: media.mediaId,
          multimediaType: mediaType,
          fileName: media.fileName,
          timeStamp: media.timeStamp,
        });
        onUpdate?.();
        mediaEventChannel.emit('onMediaUpdated');

        const [fileNamePart] = getFileParts(media.fileName ?? '');

        notify({
          variant: 'success',
          message: `"${fileNamePart}" has been categorized as "${mediaType}" under "${categoryName}".`,
        });
      } catch (error) {
        console.error('Failed to update media type:', error);
      } finally {
        // Delay resetting to allow menu close animation to complete
        setTimeout(() => {
          setIsUpdating(false);
        }, 300);
      }
    }
  };

  const disabled = isUpdating || !isEditable;

  return (
    <>
      <ToolTip title="The system files cannot be edited" hide={isEditable}>
        <span className={clsx({ 'opacity-50 cursor-not-allowed': !isEditable })}>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            className="!p-1"
            disabled={disabled}
          >
            {isUpdating ? (
              <Spinner size={20} />
            ) : (
              <MoreVertIcon className="!w-5 !h-5 text-gray-600" />
            )}
          </IconButton>
        </span>
      </ToolTip>
      <MediaTypeMenu
        value={media?.multimediaType ?? ''}
        onChange={onMediaTypeChange}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        title={(
          <>
            <MenuItem disabled className="!text-xs !font-semibold !text-gray-700 !opacity-100">
              {isUpdating ? 'Updating Category...' : 'Change category to'}
            </MenuItem>
            <Divider className="!my-0.5" />
          </>
        )}
      />
    </>
  );
}

export const CardSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 h-full w-full flex flex-col">
    <div className="p-4 flex-1 flex gap-3 w-full">
      <div className="flex-shrink-0 mt-0.5">
        <Skeleton className="!w-5 !h-5 !rounded-md flex-shrink-0" />
      </div>
      <div className="flex-1 flex flex-col min-w-0 gap-2">
        <div className="flex items-start justify-between">
          <div className=" flex flex-col gap-1 flex-1 min-w-0">
            <Skeleton className="!w-3/4 !h-[16px]" />
            <Skeleton className="!w-1/2 !h-[12px]" />
          </div>
          <Skeleton className="!w-5 !h-5 !rounded-md" />
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
          <Skeleton className="!w-20 !h-[14px]" />
          <div className="flex items-center gap-1">
            <Skeleton className="!w-7 !h-7 !rounded" />
            <Skeleton className="!w-7 !h-7 !rounded" />
            <Skeleton className="!w-7 !h-7 !rounded" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

type MediaActionsProps = {
  media: MultiMediaResponse;
  variant?: 'card' | 'list';
  categories?: PatientMediaCategoryDTO[];
  onUpdate?: () => void;
  isEditable?: boolean;
};

function MediaActions({ media, variant = 'card', categories = [], onUpdate, isEditable }: MediaActionsProps) {
  const { onMediaDelete } = useDeleteMediaHandler();
  const notify = useNotify();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleMediaDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onMediaDelete({
      list: [{
        originatorType: media.originatorType,
        mediaId: media.mediaId,
        fileName: media.fileName,
      }],
    });
  };

  const handleMediaSignature = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    mediaEventChannel.emit('onMediaSignatureDialogOpen', media);
  };

  const handleDownload = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!isDownloading) {
      if (isNil(media?.mediaId)) {
        notify({
          variant: 'error',
          message: 'File is no longer available. Refresh and try again.',
        });
      } else {
        setIsDownloading(true);
        try {
          const response = await getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId(
            media.mediaId,
          );
          const downloadUrl = head(response?.list)?.url?.value;

          if (isNil(downloadUrl)) {
            notify({
              variant: 'error',
              message: 'File is no longer available. Refresh and try again.',
            });
          } else {
            await downloadFileFromUrl(downloadUrl, media?.fileName ?? 'download');
          }
        } catch (error) {
          console.error('Failed to download media:', error);
          notify({
            variant: 'error',
            message: getErrorMessage(error) ?? 'Failed to download file',
          });
        } finally {
          setIsDownloading(false);
        }
      }
    }
  };

  const { signature, isSignable } = media;

  return (
    <div className="flex justify-end items-center w-full">
      {isNil(signature) && (
        <ToolTip title={isSignable ? 'Sign document' : 'Only PDFs can be signed'}>
          <span>
            <IconButton
              onClick={handleMediaSignature}
              disabled={!isSignable}
              size="small"
              color="info"
              className="!pointer-events-auto disabled:opacity-25 disabled:!cursor-not-allowed !p-1.5"
            >
              <SignatureIcon className="text-gray-500 !w-4 !h-4" />
            </IconButton>
          </span>
        </ToolTip>
      )}
      {!isNil(signature) && (
        <ToolTip title={`Signed by ${signature.signedBy} on ${formatDateTime(signature.signedOn!)}`}>
          <div className="p-[5px]">
            <CheckCircleIcon className="text-green-600 !w-5 !h-5" />
          </div>
        </ToolTip>
      )}
      <ToolTip
        title="Download"
        key={`download-${isDownloading}`}
      >
        <IconButton
          size="small"
          onClick={handleDownload}
          className="!p-1.5"
          disabled={isDownloading}
        >
          {isDownloading ? (
            <CircularProgress size={16} className="text-gray-600" />
          ) : (
            <DownloadIcon className="!w-4 !h-4 text-gray-600" />
          )}
        </IconButton>
      </ToolTip>
      <ToolTip title="Delete">
        <span>
          <IconButton
            size="small"
            color="error"
            className="nw-multimedia-delete-button"
            onClick={handleMediaDelete}
          >
            <DeleteIcon className="text-black !w-5 !h-5" />
          </IconButton>
        </span>
      </ToolTip>
      {variant === 'list' && (
        <CategoryMenu
          media={media}
          categories={categories}
          onUpdate={onUpdate}
          isEditable={isEditable ?? false}
        />
      )}
    </div>
  );
}

type MultimediaCardProps = {
  media: MultiMediaResponse;
  categories: PatientMediaCategoryDTO[];
  refetch?: () => void;
  highlightedMediaId?: number | null;
};

export function MultimediaCard({
  media,
  categories,
  refetch,
  highlightedMediaId,
}: MultimediaCardProps) {
  const OnUpdate = () => {
    refetch?.();
  };

  const { fileName, multimediaType, timeStamp } = media;

  const fileParts = getFileParts(fileName ?? '');
  const [fileNamePart, extension] = fileParts;

  const isHighlighted = highlightedMediaId === media?.mediaId;

  const multimediaByTypes = useMemo(
    () => keyBy(map(categories, (category) => category?.mediaTypes).flat(), 'value'),
    [categories],
  );

  const isEditable = useMemo(
    () => multimediaByTypes[media.multimediaType ?? '']?.isUploadableFromChart,
    [media.multimediaType, multimediaByTypes],
  );

  const { mediaTypeDisplayNameLookUp = {} } = useGetMediaSettings();
  const mediaTypeDisplayName = mediaTypeDisplayNameLookUp?.[multimediaType!] ?? multimediaType;

  return (
    <div className={clsx(
      'bg-white rounded-lg border shadow-sm hover:shadow-md hover:!bg-gray-50 transition-all duration-500 h-full w-full flex flex-col',
      { 'border-blue-500 border-2 ring-2 ring-blue-200 shadow-lg': isHighlighted },
      { 'border-gray-200': !isHighlighted },
    )}
    >
      <div className="p-4 flex-1 flex gap-3 w-full">
        <div className="flex-shrink-0 mt-0.5">
          {getFileIcon(fileName)}
        </div>
        <div className="flex-1 flex flex-col min-w-0 gap-2">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <ToolTip title={fileNamePart ?? ''}>
                <p className="font-semibold text-sm truncate text-gray-900 nw-multimedia-file-name">
                  {fileNamePart ?? '--'}
                </p>
              </ToolTip>
              <p className="text-xs text-gray-500 nw-multimedia-type">
                {mediaTypeDisplayName ?? '--'}
                {' '}
                •
                {' '}
                {extension?.toUpperCase() ?? '--'}
              </p>
            </div>
            <CategoryMenu
              media={media}
              categories={categories}
              onUpdate={OnUpdate}
              isEditable={isEditable ?? false}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
            <span>{formatDate(timeStamp ?? '')}</span>
            <MediaActions
              media={media}
              variant="card"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

type MediaCellProps = {
  media: MultiMediaResponse;
};

function CardViewCell({ media }: MediaCellProps) {
  const {
    categories,
    refetch,
  } = useTableContext<MultimediaContextType>();

  return (
    <MultimediaCard
      media={media}
      categories={categories}
      refetch={refetch}
    />
  );
}

export const cardViewColumns: ColumnDef<MultiMediaResponse>[] = [
  {
    id: 'fileName',
    accessorKey: 'fileName',
    header: '',
    skeleton: <CardSkeleton />,
    cell: ({ row: { original } }) => <CardViewCell media={original} />,
  },
];

type MultimediaTypeCellProps = {
  multimediaType?: string | null;
};

function MultimediaTypeCell({ multimediaType }: MultimediaTypeCellProps) {
  const { mediaTypeDisplayNameLookUp = {} } = useGetMediaSettings();
  const mediaTypeDisplayName = mediaTypeDisplayNameLookUp?.[multimediaType!] ?? multimediaType;

  return (
    <span className="truncate nw-multimedia-type">{mediaTypeDisplayName}</span>
  );
}

function ListActionsCell({ media }: MediaCellProps) {
  const { categories, refetch } = useTableContext<MultimediaContextType>();
  const onUpdate = () => {
    refetch?.();
  };
  const multimediaByTypes = keyBy(map(categories, (category) => category?.mediaTypes).flat(), 'value');
  const isEditable = multimediaByTypes[media?.multimediaType ?? '']?.isUploadableFromChart;

  return (
    <MediaActions
      media={media}
      variant="list"
      categories={categories}
      onUpdate={onUpdate}
      isEditable={isEditable ?? false}
    />
  );
}

export const listViewColumns: ColumnDef<MultiMediaResponse>[] = [
  {
    id: 'date',
    size: 90,
    header: 'Date',
    cell: ({ row }) => (
      <span>{formatDate(row.original.timeStamp ?? '')}</span>
    ),
  }, {
    id: 'fileName',
    meta: { autoSized: true },
    header: 'Name',
    cell: ({ row }) => (
      <div className="truncate font-semibold nw-multimedia-file-name">
        <ToolTip title={row.original.fileName}>
          <span className="w-fit">
            {row.original.fileName}
          </span>
        </ToolTip>
      </div>
    ),
  }, {
    id: 'type',
    size: 250,
    header: 'Type',
    cell: ({ row: { original: { multimediaType } } }) => (
      <MultimediaTypeCell multimediaType={multimediaType} />
    ),
  }, {
    id: 'timeStamp',
    size: 100,
    minSize: 80,
    columnTitle: 'Timestamp',
    cell: ({ row: { original: { timeStamp } } }) => (
      formatDateTime(timeStamp ?? '')
    ),
  }, {
    id: 'actions',
    header: '',
    size: 140,
    cell: ({ row: { original } }) => <ListActionsCell media={original} />,
  },
];
